// Navegação da área de condomínio — fonte única consumida pelo menu lateral
// (Sidebar), pelos separadores de cada módulo (ModuleTabs) e pelos guards de
// rota (CondominiumLayout). Ver docs/ux-audit/PLAN.md §2.4–2.5.
//
// Modelo: módulo → separadores (máx. 6) → páginas. Um separador com mais de
// uma página mostra uma sub-navegação segmentada; cada página tem a sua rota
// (as rotas antigas mantêm-se todas). Isto é só UI: as permissões verdadeiras
// estão no backend.
import type { SvgIconComponent } from '@mui/icons-material'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined'
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocalParkingOutlinedIcon from '@mui/icons-material/LocalParkingOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined'
import PoolOutlinedIcon from '@mui/icons-material/PoolOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import type { Permissao, SubscricaoModulo } from '../utils/types'

/** Uma página (rota) — `path` é relativo a /properties/:id ('' = painel). */
export interface NavLeaf {
  id: string
  /** Chave i18n `ns:chave`. */
  label: string
  path: string
  /** Basta UMA (o equivalente ao @PreAuthorize do GET/list que a página usa). Sem lista = sempre visível. */
  permissions?: Permissao[]
  /** Arrendamento: só para quem é (ou foi) proprietário / inquilino — ver useRentalOwnerAccess/useRentalTenantAccess. */
  rental?: 'owner' | 'tenant'
  /** Só se o condomínio tem edifícios / blocos. */
  requires?: 'predios' | 'blocos'
  /**
   * Finanças do condomínio: um condómino só as vê no modo "Ver como comissão" (decisão de produto, 2026-10-27) —
   * ou, numa propriedade, se for proprietário (lê as finanças da propriedade toda; decisão de 2026-11-05).
   */
  committeeFinance?: boolean
  /** Escondida numa propriedade (estate PROPERTIES), que não tem comissão — ex.: a Comissão. */
  condominiumOnly?: boolean
  /** Só existe numa propriedade — ex.: a prestação de contas ao proprietário. */
  propertyOnly?: boolean
  /** Prestação de contas: a gestora (todos os extractos) ou quem é/foi proprietário (o seu) — ver usePropertyAccess. */
  ownerStatement?: boolean
  /** Página aberta a partir de outra (ex.: configuração de SLA a partir dos Pedidos): activa o separador, mas não aparece na sub-navegação. */
  hiddenInNav?: boolean
}

export interface NavTab {
  id: string
  label: string
  leaves: NavLeaf[]
}

export type NavSection = 'gestao' | 'comunidade' | 'administracao' | 'principal' | 'mais'

export interface NavModule {
  id: string
  label: string
  icon: SvgIconComponent
  section: NavSection
  /** Módulo da subscrição — sem ele no plano, o módulo inteiro desaparece. */
  modulo?: SubscricaoModulo
  /** Só faz sentido a quem tem entidade (self-service, ex.: Arrendamentos). */
  memberOnly?: boolean
  /** Escondido numa propriedade (estate PROPERTIES): só existe em propriedade horizontal (ex.: Assembleias). */
  condominiumOnly?: boolean
  tabs: NavTab[]
  /** Contador no menu (ver useNavBadges). */
  badge?: 'financeOverdueUnits'
}

const leaf = (id: string, label: string, path: string, permissions?: Permissao[], extra: Partial<NavLeaf> = {}): NavLeaf =>
  ({ id, label, path, permissions, ...extra })
const tab = (id: string, label: string, leaves: NavLeaf[]): NavTab => ({ id, label, leaves })
/** Separador com uma só página — o rótulo do separador é o da página. */
const single = (l: NavLeaf): NavTab => tab(l.id, l.label, [l])

/** As páginas do lado do proprietário em Arrendamento: o proprietário (auto-serviço) ou a gestora numa propriedade. */
const RENTAL_OWNER_SIDE: Permissao[] = ['ARRENDAMENTO_GERIR_PROPRIO', 'ARRENDAMENTO_READ', 'ARRENDAMENTO_MANAGE', 'ARRENDAMENTO_PAGAMENTO_APROVAR']

// ── Páginas ───────────────────────────────────────────────────────────────────
// Permissões alinhadas com as do menu anterior (ver o histórico de Sidebar.tsx
// para o porquê de cada uma) — mudar aqui muda o menu, os separadores e os guards.
export const L = {
  dashboard: leaf('dashboard', 'nav:sidebar.dashboard', '', ['CONDOMINIO_READ', 'ESTATE_READ']),

  units: leaf('units', 'nav:sidebar.units', 'units', ['CONDOMINIO_READ', 'ESTATE_READ', 'UNIDADE_READ', 'UNIDADE_GERIR_PROPRIA', 'UNIDADE_ASSOCIAR_ENTIDADE_PROPRIA']),
  residents: leaf('residents', 'nav:sidebar.residents', 'residents', ['ENTIDADE_READ']),
  buildings: leaf('buildings', 'nav:sidebar.buildings', 'buildings', ['CONDOMINIO_READ', 'ESTATE_READ'], { requires: 'predios' }),
  blocks: leaf('blocks', 'nav:sidebar.blocks', 'blocks', ['CONDOMINIO_READ', 'ESTATE_READ'], { requires: 'blocos' }),
  // Não basta ver os condóminos (ENTIDADE_READ): o Porteiro lê-os, mas não o histórico (V197).
  occupancy: leaf('occupancy', 'nav:sidebar.occupancyHistory', 'occupancy-history', ['ENTIDADE_OCUPACOES_READ', 'ENTIDADE_MANAGE']),
  committee: leaf('committee', 'nav:sidebar.committee', 'committee', ['COMISSAO_READ', 'COMISSAO_MANAGE'], { condominiumOnly: true }),

  financeOverview: leaf('finance-overview', 'nav:tabs.overview', 'finance/overview', ['FINANCAS_READ'], { committeeFinance: true }),
  charges: leaf('finance-charges', 'nav:sidebar.financeCharges', 'finance/charges', ['COBRANCA_READ']),
  payments: leaf('finance-payments', 'nav:sidebar.financePayments', 'finance/payments', ['FINANCAS_READ']),
  // Compras não usam FINANCAS_READ: o papel de condómino tem-no e não deve ver
  // as compras do condomínio — os membros da comissão decidem a partir do painel.
  purchases: leaf('finance-purchases', 'nav:sidebar.financePurchases', 'finance/purchases', ['FINANCAS_MANAGE', 'COMPRA_READ', 'COMPRA_REGISTAR', 'COMPRA_COTAR', 'COMPRA_MANAGE'], { committeeFinance: true }),
  expenses: leaf('finance-expenses', 'nav:sidebar.financeExpenses', 'finance/expenses', ['FINANCAS_READ'], { committeeFinance: true }),
  expensePayments: leaf('finance-expense-payments', 'nav:tabs.expensePayments', 'finance/expense-payments', ['FINANCAS_READ'], { committeeFinance: true }),
  accounts: leaf('finance-accounts', 'nav:sidebar.financeAccounts', 'finance/accounts', ['FINANCAS_READ'], { committeeFinance: true }),
  // Transferências/Fornecedores não usam FINANCAS_READ sozinho: um papel só com
  // leitura genérica não tem nada para fazer aqui.
  transfers: leaf('finance-transfers', 'nav:sidebar.financeTransfers', 'finance/transfers', ['FINANCAS_MANAGE', 'FINANCAS_CRIAR', 'TRANSFERENCIA_EXECUTAR'], { committeeFinance: true }),
  incomes: leaf('finance-incomes', 'nav:tabs.recurringIncomes', 'finance/incomes', ['FINANCAS_READ'], { committeeFinance: true }),
  // Despesas recorrentes: os modelos do motor de geração automática de despesas.
  recurringExpenses: leaf('finance-recurring-expenses', 'nav:tabs.recurringExpenses', 'finance/recurring-expenses', ['FINANCAS_READ'], { committeeFinance: true }),
  // Prestação de contas ao proprietário (só numa propriedade): a gestora vê os extractos de todos; o proprietário, o seu.
  ownerStatements: leaf('finance-owner-statements', 'nav:sidebar.ownerStatements', 'finance/owner-statements', undefined, { propertyOnly: true, ownerStatement: true }),
  suppliers: leaf('suppliers', 'nav:sidebar.financeSuppliers', 'finance/suppliers', ['FINANCAS_MANAGE', 'FINANCAS_CRIAR', 'FORNECEDOR_READ', 'FORNECEDOR_CRIAR', 'FORNECEDOR_EDITAR', 'FORNECEDOR_ELIMINAR', 'FORNECEDOR_MANAGE'], { committeeFinance: true }),

  accessOverview: leaf('access-overview', 'nav:tabs.overview', 'access-control/overview', ['VISITA_READ', 'AGENDAMENTO_VISITA_READ', 'VEICULO_READ', 'ENCOMENDA_READ']),
  visitsLog: leaf('access-visits-log', 'nav:tabs.visitsLog', 'access-control/visits-log', ['VISITA_READ']),
  visitsScheduling: leaf('access-visits-scheduling', 'nav:tabs.visitsScheduling', 'access-control/visits', ['AGENDAMENTO_VISITA_READ', 'AGENDAMENTO_VISITA_READ_PROPRIO', 'AGENDAMENTO_VISITA_MANAGE']),
  visitors: leaf('access-visitors', 'nav:sidebar.accessControlVisitors', 'access-control/visitors', ['VISITANTE_READ']),
  vehicles: leaf('access-vehicles', 'nav:tabs.vehicles', 'access-control/vehicles', ['VEICULO_READ']),
  // Histórico de TODOS os veículos não usa VEICULO_READ — esse cobre só a ficha do próprio.
  vehiclesLog: leaf('access-vehicles-log', 'nav:tabs.history', 'access-control/vehicles-log', ['VEICULO_MANAGE', 'VEICULO_HISTORICO_READ']),
  packages: leaf('access-packages', 'nav:sidebar.accessControlPackages', 'access-control/packages', ['ENCOMENDA_READ']),
  accessPoints: leaf('access-points', 'nav:sidebar.accessControlAccessPoints', 'access-control/access-points', ['PONTO_ACESSO_READ', 'PONTO_ACESSO_MANAGE']),
  shifts: leaf('access-shifts', 'nav:sidebar.accessControlShifts', 'access-control/shifts', ['TURNO_READ', 'TURNO_MANAGE']),

  maintenanceOverview: leaf('maintenance-overview', 'nav:tabs.overview', 'maintenance/overview', ['MANUTENCAO_READ']),
  // Relatório (disponibilidade e custos): a gestão com MANUTENCAO_RELATORIO_READ; um condómino só no modo comissão
  // (ou, numa propriedade, o proprietário) — os mesmos que lêem as finanças (RelatorioManutencaoService).
  maintenanceReport: leaf('maintenance-report', 'nav:tabs.report', 'maintenance/report', ['MANUTENCAO_RELATORIO_READ'], { committeeFinance: true }),
  serviceOrders: leaf('maintenance-orders', 'nav:tabs.serviceOrders', 'maintenance/service-orders', ['MANUTENCAO_READ']),
  // Planeamento preventivo não é visível a condóminos (nunca MANUTENCAO_READ sozinho).
  schedules: leaf('maintenance-schedules', 'nav:tabs.preventivePlans', 'maintenance/schedules', ['MANUTENCAO_MANAGE', 'AGENDAMENTO_MANUTENCAO_READ']),
  assets: leaf('maintenance-assets', 'nav:tabs.equipment', 'maintenance/assets', ['MANUTENCAO_READ']),
  requests: leaf('maintenance-requests', 'nav:tabs.requests', 'maintenance/requests', ['MANUTENCAO_READ']),
  slaConfig: leaf('maintenance-sla', 'nav:tabs.requests', 'maintenance/sla-config', ['MANUTENCAO_SLA_MANAGE'], { hiddenInNav: true }),

  parkingAreas: leaf('parking-areas', 'nav:sidebar.parkingAreas', 'parking', ['PARQUE_ESTACIONAMENTO_READ']),
  parkingSlots: leaf('parking-slots', 'nav:sidebar.parkingSlots', 'parking/slots', ['VAGA_ESTACIONAMENTO_READ']),
  parkingAssignments: leaf('parking-assignments', 'nav:sidebar.parkingAssignments', 'parking/assignments', ['ATRIBUICAO_ESTACIONAMENTO_READ']),
  parkingAuthorizations: leaf('parking-authorizations', 'nav:sidebar.parkingAuthorizations', 'parking/authorizations', ['AUTORIZACAO_ESTACIONAMENTO_READ', 'AUTORIZACAO_ESTACIONAMENTO_CRIAR_PROPRIA']),

  commonAreas: leaf('common-areas', 'nav:tabs.spaces', 'common-areas', ['ESPACO_COMUM_READ']),
  // "Minhas reservas" só a quem pode reservar — quem só consulta (ex: Porteiro) vê calendário e histórico.
  myReservations: leaf('common-areas-mine', 'nav:tabs.myReservations', 'common-areas/my-reservations', ['RESERVA_CRIAR', 'RESERVA_MANAGE']),
  approvals: leaf('common-areas-approvals', 'nav:tabs.approvals', 'common-areas/approvals', ['RESERVA_MANAGE']),
  calendar: leaf('common-areas-calendar', 'nav:sidebar.commonAreasCalendar', 'common-areas/calendar', ['RESERVA_READ']),
  reservationsHistory: leaf('common-areas-history', 'nav:tabs.history', 'common-areas/history', ['RESERVA_READ']),

  // Do lado do proprietário — e, numa propriedade, da gestora (ARRENDAMENTO_READ/MANAGE/PAGAMENTO_APROVAR), que
  // gere os contratos de todos os proprietários (ver useRentalManagement).
  rentalsContracts: leaf('rentals-contracts', 'rentals:menu.meusArrendamentos', 'arrendamentos/meus', RENTAL_OWNER_SIDE, { rental: 'owner' }),
  rentalsCharges: leaf('rentals-charges', 'rentals:menu.cobrancasRenda', 'arrendamentos/cobrancas', RENTAL_OWNER_SIDE, { rental: 'owner' }),
  rentalsReceipts: leaf('rentals-receipts', 'rentals:menu.recebimentos', 'arrendamentos/recebimentos', RENTAL_OWNER_SIDE, { rental: 'owner' }),
  myRental: leaf('rentals-mine', 'rentals:menu.meuArrendamento', 'arrendamentos/meu-arrendamento', ['ARRENDAMENTO_GERIR_PROPRIO', 'ARRENDAMENTO_CONSULTAR_PROPRIO'], { rental: 'tenant' }),
  myRentalAccount: leaf('rentals-account', 'rentals:menu.minhasFaturas', 'arrendamentos/faturas', ['ARRENDAMENTO_GERIR_PROPRIO', 'ARRENDAMENTO_CONSULTAR_PROPRIO'], { rental: 'tenant' }),

  comunicados: leaf('communication-comunicados', 'nav:sidebar.communicationComunicados', 'communication/comunicados', ['COMUNICADO_READ']),
  // Modelos são um catálogo à parte — quem só lê comunicados (ex: Porteiro) não os vê.
  templates: leaf('communication-templates', 'nav:tabs.templates', 'communication/templates', ['COMUNICADO_MANAGE', 'COMUNICADO_CRIAR', 'COMUNICADO_EDITAR', 'COMUNICADO_TEMPLATE_READ']),
  documents: leaf('documents', 'nav:sidebar.documents', 'documents', ['DOCUMENTO_READ']),

  assembleias: leaf('assembleias', 'nav:sidebar.assembleias', 'assembleias', ['ASSEMBLEIA_READ']),
  votacoes: leaf('assembleias-votacoes', 'nav:sidebar.assembleiasVotacoes', 'assembleias/votacoes', ['VOTACAO_READ']),

  users: leaf('users', 'nav:sidebar.users', 'users', ['USERS_READ']),
} satisfies Record<string, NavLeaf>

// ── Árvore da gestão (todos os módulos) ─────────────────────────────────────────
export const MANAGEMENT_NAV: NavModule[] = [
  { id: 'dashboard', label: 'nav:sidebar.dashboard', icon: DashboardOutlinedIcon, section: 'gestao', tabs: [single(L.dashboard)] },
  {
    id: 'condominium', label: 'nav:sidebar.condominium', icon: ApartmentOutlinedIcon, section: 'gestao',
    tabs: [single(L.units), single(L.residents), tab('structure', 'nav:tabs.buildingsAndBlocks', [L.buildings, L.blocks]), tab('occupancy', 'nav:tabs.occupancy', [L.occupancy]), single(L.committee)],
  },
  {
    id: 'finance', label: 'nav:sidebar.finance', icon: AccountBalanceWalletOutlinedIcon, section: 'gestao', modulo: 'FINANCAS', badge: 'financeOverdueUnits',
    tabs: [
      single(L.financeOverview),
      tab('charges', 'nav:sidebar.financeCharges', [L.charges, L.payments]),
      // Fornecedores ao lado dos pagamentos a fornecedores (antes era um módulo em Administração).
      tab('expenses', 'nav:sidebar.financeExpenses', [L.purchases, L.expenses, L.expensePayments, L.suppliers]),
      tab('accounts', 'nav:sidebar.financeAccounts', [L.accounts, L.transfers]),
      tab('config', 'nav:tabs.settings', [L.incomes, L.recurringExpenses]),
      single(L.ownerStatements),
    ],
  },
  {
    id: 'access-control', label: 'nav:sidebar.accessControl', icon: BadgeOutlinedIcon, section: 'gestao', modulo: 'CONTROLO_ACESSO',
    tabs: [
      single(L.accessOverview),
      tab('visits', 'nav:sidebar.accessControlVisits', [L.visitsLog, L.visitsScheduling]),
      single(L.visitors),
      tab('vehicles', 'nav:tabs.vehicles', [L.vehicles, L.vehiclesLog]),
      single(L.packages),
      tab('config', 'nav:tabs.settings', [L.accessPoints, L.shifts]),
    ],
  },
  {
    id: 'maintenance', label: 'nav:sidebar.maintenance', icon: HandymanOutlinedIcon, section: 'gestao', modulo: 'MANUTENCAO',
    tabs: [single(L.maintenanceOverview), single(L.maintenanceReport), single(L.serviceOrders), single(L.schedules), single(L.assets), tab('requests', 'nav:tabs.requests', [L.requests, L.slaConfig])],
  },
  {
    id: 'parking', label: 'nav:sidebar.parking', icon: LocalParkingOutlinedIcon, section: 'gestao', modulo: 'ESTACIONAMENTO',
    tabs: [single(L.parkingAreas), single(L.parkingSlots), single(L.parkingAssignments), single(L.parkingAuthorizations)],
  },
  {
    id: 'common-areas', label: 'nav:sidebar.commonAreas', icon: PoolOutlinedIcon, section: 'comunidade', modulo: 'AREAS_COMUNS',
    tabs: [single(L.commonAreas), single(L.myReservations), single(L.approvals), single(L.calendar), single(L.reservationsHistory)],
  },
  {
    id: 'communication', label: 'nav:sidebar.communication', icon: CampaignOutlinedIcon, section: 'comunidade', modulo: 'COMUNICACAO',
    tabs: [single(L.comunicados), single(L.templates), single(L.documents)],
  },
  {
    id: 'assembleias', label: 'nav:sidebar.assembleias', icon: GroupsOutlinedIcon, section: 'comunidade', modulo: 'ASSEMBLEIAS', condominiumOnly: true,
    tabs: [single(L.assembleias), single(L.votacoes)],
  },
  {
    id: 'rentals', label: 'rentals:title', icon: HomeWorkOutlinedIcon, section: 'comunidade', modulo: 'ARRENDAMENTO', memberOnly: true,
    tabs: [single(L.rentalsContracts), single(L.rentalsCharges), single(L.rentalsReceipts), single(L.myRental), single(L.myRentalAccount)],
  },
  { id: 'users', label: 'nav:sidebar.users', icon: PeopleOutlineOutlinedIcon, section: 'administracao', tabs: [single(L.users)] },
]

// ── Árvore do condómino ─────────────────────────────────────────────────────────
// Briefing 4.4: Início, As minhas unidades, Pagamentos, Reservas, Visitas,
// Pedidos de manutenção, Comunicados, Assembleias. O resto a que já tinha
// acesso continua em "Mais" (nada é removido), excepto as finanças do
// condomínio, que só a comissão vê.
export const RESIDENT_NAV: NavModule[] = [
  { id: 'home', label: 'nav:resident.home', icon: HomeOutlinedIcon, section: 'principal', tabs: [single(L.dashboard)] },
  { id: 'my-units', label: 'nav:resident.myUnits', icon: MeetingRoomOutlinedIcon, section: 'principal', tabs: [single(L.units)] },
  {
    id: 'payments', label: 'nav:resident.payments', icon: PaymentsOutlinedIcon, section: 'principal', modulo: 'FINANCAS',
    tabs: [tab('charges', 'nav:sidebar.financeCharges', [L.charges]), tab('payments', 'nav:resident.myPayments', [L.payments])],
  },
  {
    id: 'reservations', label: 'nav:resident.reservations', icon: EventAvailableOutlinedIcon, section: 'principal', modulo: 'AREAS_COMUNS',
    tabs: [single(L.myReservations), single(L.calendar), single(L.commonAreas)],
  },
  {
    // "Minhas visitas": o condómino agenda e vê o registo das SUAS visitas (o backend só lhe devolve
    // essas); registar entradas é da portaria.
    id: 'visits', label: 'nav:resident.visits', icon: HowToRegOutlinedIcon, section: 'principal', modulo: 'CONTROLO_ACESSO',
    tabs: [tab('scheduling', 'nav:tabs.visitsScheduling', [L.visitsScheduling]), tab('log', 'nav:tabs.visitsLog', [L.visitsLog])],
  },
  {
    id: 'maintenance-requests', label: 'nav:resident.maintenanceRequests', icon: HandymanOutlinedIcon, section: 'principal', modulo: 'MANUTENCAO',
    // O relatório: só no modo comissão (ou ao proprietário, numa propriedade) — o papel Entidade não tem a permissão.
    tabs: [tab('requests', 'nav:tabs.requests', [L.requests]), single({ ...L.maintenanceReport, permissions: ['MANUTENCAO_READ'] })],
  },
  {
    id: 'communication', label: 'nav:sidebar.communicationComunicados', icon: CampaignOutlinedIcon, section: 'principal', modulo: 'COMUNICACAO',
    tabs: [single(L.comunicados), single(L.documents)],
  },
  {
    id: 'assembleias', label: 'nav:sidebar.assembleias', icon: GroupsOutlinedIcon, section: 'principal', modulo: 'ASSEMBLEIAS', condominiumOnly: true,
    tabs: [single(L.assembleias), single(L.votacoes)],
  },
]

/** Finanças em leitura para o membro da comissão ("Ver como comissão"). */
export const COMMITTEE_FINANCE_NAV: NavModule = {
  id: 'committee-finance', label: 'nav:resident.committeeFinance', icon: AccountBalanceWalletOutlinedIcon, section: 'principal', modulo: 'FINANCAS',
  tabs: [
    single(L.financeOverview),
    tab('expenses', 'nav:sidebar.financeExpenses', [L.purchases, L.expenses, L.expensePayments, L.suppliers]),
    tab('accounts', 'nav:sidebar.financeAccounts', [L.accounts, L.transfers]),
    tab('config', 'nav:tabs.settings', [L.incomes, L.recurringExpenses]),
  ],
}

/**
 * Finanças da propriedade para o proprietário (decisão de 2026-11-05): só leitura, a propriedade toda — como
 * a comissão num condomínio, mais as compras (as cotações e a adjudicação são a prova de que se gastou bem) e
 * o seu extracto. Sempre visível ao proprietário; não há interruptor como o "Ver como comissão".
 */
export const OWNER_FINANCE_NAV: NavModule = {
  id: 'owner-finance', label: 'nav:resident.propertyFinance', icon: AccountBalanceWalletOutlinedIcon, section: 'principal', modulo: 'FINANCAS',
  tabs: [
    single(L.ownerStatements),
    single(L.financeOverview),
    // Sem permissões de compras: o servidor deixa o proprietário ler (AcessoFinancasService#souProprietarioNaPropriedade).
    tab('expenses', 'nav:sidebar.financeExpenses', [{ ...L.purchases, permissions: ['FINANCAS_READ'] }, L.expenses, L.expensePayments, L.suppliers]),
    tab('accounts', 'nav:sidebar.financeAccounts', [L.accounts, L.transfers]),
    tab('config', 'nav:tabs.settings', [L.incomes, L.recurringExpenses]),
  ],
}

// ── Árvore da portaria ──────────────────────────────────────────────────────────
// Briefing 4.4: Visitas, Viaturas, Encomendas — optimizada para ecrã pequeno.
export const DOORMAN_NAV: NavModule[] = [
  { id: 'home', label: 'nav:resident.home', icon: HomeOutlinedIcon, section: 'principal', tabs: [single(L.dashboard)] },
  {
    id: 'visits', label: 'nav:sidebar.accessControlVisits', icon: HowToRegOutlinedIcon, section: 'principal', modulo: 'CONTROLO_ACESSO',
    tabs: [single(L.visitsLog), single(L.visitsScheduling), single(L.visitors)],
  },
  {
    // Para o porteiro, "Histórico" não dizia o que lá se faz: é onde regista as entradas e saídas.
    id: 'vehicles', label: 'nav:tabs.vehicles', icon: DirectionsCarFilledOutlinedIcon, section: 'principal', modulo: 'CONTROLO_ACESSO',
    tabs: [single(L.vehicles), single({ ...L.vehiclesLog, label: 'nav:tabs.vehicleMovements' })],
  },
  { id: 'packages', label: 'nav:sidebar.accessControlPackages', icon: Inventory2OutlinedIcon, section: 'principal', modulo: 'CONTROLO_ACESSO', tabs: [single(L.packages)] },
  { id: 'shifts', label: 'nav:sidebar.accessControlShifts', icon: ScheduleOutlinedIcon, section: 'principal', modulo: 'CONTROLO_ACESSO', tabs: [single(L.shifts), single(L.accessPoints)] },
]
